import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import styles from './UserProfile.module.css';
import { personIcon } from '../../assets/icons';
import { FaEllipsisH } from 'react-icons/fa';
import EditProfileModal from '../utils/EditProfileModal';
import SharableUsername from '../utils/SharableUsername';
import ProfileLoading from '../utils/ProfileLoading';
import EditableField from '../utils/EditableField';
import { useEditableField } from '../../hooks/useEditableField';
import modalStyles from '../utils/EditProfileModal.module.css';
import OwnedTabCharacters from './OwnedTabCharacters';
import OwnedTabPersonas from './OwnedTabPersonas';
import OwnedTabScenarios from './OwnedTabScenarios';
import type { CharacterListResponse, PersonaListResponse, UpdateUserProfilePayload } from '../../api/types';
import { UserProfile as UserProfileClass } from '../../api/users';
import { apiClient } from '../../api/client';
import DesktopUserProfile from './DesktopUserProfile';
import MobileUserProfile from './MobileUserProfile';

interface LocationState {
  fromChatId?: string;
}

export interface ProfileComponentProps {
  profileData: UserProfileClass | null;
  isCurrentUser: boolean;
  isUpdating: boolean;
  displayName: ReturnType<typeof useEditableField>;
  usernameField: ReturnType<typeof useEditableField>;
  activeTab: 'characters' | 'personas' | 'scenarios';
  characters: CharacterListResponse[];
  personas: PersonaListResponse[];
  scenarios: any[];
  tabsLoading: boolean;
  menuOpen: boolean;
  handleIconChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleSignOut: () => Promise<void>;
  toggleMenu: () => void;
  setActiveTab: React.Dispatch<React.SetStateAction<'characters' | 'personas' | 'scenarios'>>;
  refreshCharacters: () => Promise<void>;
  refreshPersonas: () => Promise<void>;
  refreshScenarios: () => Promise<void>;
  fromChatId?: string;
  handleBack: () => void;
  loading: boolean;
}

function UserProfile(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useParams<{ username: string }>();
  const { setIsLoggedIn, user, refreshUser } = useAuth();
  const [profileData, setProfileData] = useState<UserProfileClass | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [updatedUsername, setUpdatedUsername] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'characters' | 'personas' | 'scenarios'>('characters');
  const [characters, setCharacters] = useState<CharacterListResponse[]>([]);
  const [personas, setPersonas] = useState<PersonaListResponse[]>([]);
  const [scenarios, setScenarios] = useState<any[]>([]);
  const [tabsLoading, setTabsLoading] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [tempIconUrl, setTempIconUrl] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);

  // Determine if we came from a chat
  const locationState = location.state as LocationState | null;
  const fromChatId = locationState?.fromChatId;

  // Helper function to ensure we always have the update method
  const safeUpdate = useCallback(async (data: UpdateUserProfilePayload): Promise<UserProfileClass> => {
    if (!profileData || !profileData.update) {
      throw new Error('Profile update function not available');
    }
    
    console.log('Safe update called with data:', data);
    
    // Call the update function directly from the profile object
    const updatedProfile = await profileData.update(data);
    
    console.log('Profile updated, new profile:', updatedProfile);
    
    // Replace the entire profile object with the new instance
    setProfileData(updatedProfile);
    
    // If username or icon was updated, refresh the auth context
    if (data.username || data.icon) {
      if (refreshUser) {
        console.log('Refreshing user in Auth context');
        await refreshUser();
      }
    }
    
    return updatedProfile;
  }, [profileData, refreshUser]);

  const displayName = useEditableField({
    initialValue: profileData?.display_name || '',
    fieldName: 'display name',
    onUpdate: async (newValue: string) => {
      await safeUpdate({ display_name: newValue.trim() });
    },
    validate: (value: string) => {
      if (!value.trim()) return 'Display name cannot be empty';
      return undefined;
    }
  });

  const usernameField = useEditableField({
    initialValue: username || '',
    fieldName: 'username',
    onUpdate: async (newValue: string) => {
      const trimmedUsername = newValue.trim();
      const updatedProfile = await safeUpdate({ username: trimmedUsername });
      setUpdatedUsername(updatedProfile.username);
      
      // After successful username update, navigate to new URL
      navigate(`/users/${updatedProfile.username}`, { replace: true });
    },
    validate: (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return 'Username cannot be empty';
      if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
        return 'Username can only contain letters, numbers, and underscores';
      }
      return undefined;
    }
  });

  const isCurrentUser = user?.username === username || updatedUsername === username;

  const handleIconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUpdating(true);
    
    try {
      // Store the original icon URL for potential rollback
      const originalIconUrl = profileData?.icon || '';
      
      // Create a temporary URL for immediate UI feedback
      const tempUrl = URL.createObjectURL(file);
      setTempIconUrl(tempUrl);
      
      // First update the UI with the temp URL for responsiveness
      setProfileData(prev => {
        if (!prev) return null;
        
        // Create a clone of the current profile with the temp icon
        const updatedProfile = new UserProfileClass(
          prev.username,
          apiClient,
          {
            display_name: prev.display_name,
            username: prev.username,
            icon: tempUrl
          }
        );
        
        return updatedProfile;
      });
      
      console.log('Updating icon with file:', file);
      
      // Start the actual update process
      try {
        // Use the existing update method on the profile object
        const updatedProfile = await safeUpdate({ icon: file });
        console.log('Icon update successful:', updatedProfile);
      } catch (updateError) {
        console.error('Failed to update profile icon via API:', updateError);
        
        // Revert to original icon
        setProfileData(prev => {
          if (!prev) return null;
          
          return new UserProfileClass(
            prev.username,
            apiClient,
            {
              display_name: prev.display_name,
              username: prev.username,
              icon: originalIconUrl
            }
          );
        });
        
        throw updateError; // Re-throw to be caught by outer catch block
      }
    } catch (error) {
      console.error('Icon update process failed:', error);
    } finally {
      // Clean up the temporary URL if it exists
      if (tempIconUrl) {
        URL.revokeObjectURL(tempIconUrl);
        setTempIconUrl(null);
      }
      
      setIsUpdating(false);
    }
  };

  const refreshCharacters = async (): Promise<void> => {
    try {
      const charactersData = await api.characters.owned();
      setCharacters(charactersData);
    } catch (error) {
      console.error('Failed to refresh characters:', error);
    }
  };

  const refreshScenarios = async (): Promise<void> => {
    try {
      const scenariosData = await api.scenarioDefinitions.owned();
      setScenarios(scenariosData);
    } catch (error) {
      console.error('Failed to refresh scenarios:', error);
    }
  };

  const refreshPersonas = async (): Promise<void> => {
    try {
      const personasData = await api.personas.owned();
      setPersonas(personasData);
    } catch (error) {
      console.error('Failed to refresh personas:', error);
    }
  };

  useEffect(() => {
    async function fetchProfile() {
      try {
        if (isCurrentUser) {
          // For current user, we need to convert the user object to UserProfileClass
          if (user) {
            const userProfile = await api.users.get(user.username);
            setProfileData(userProfile);
          }
          
          // Fetch characters and personas for current user
          setTabsLoading(true);
          const [charactersData, personasData, scenariosData] = await Promise.all([
            api.characters.owned(),
            api.personas.owned(),
            api.scenarioDefinitions.owned()
          ]);
          setCharacters(charactersData);
          setPersonas(personasData);
          setScenarios(scenariosData);
          setTabsLoading(false);
        } else {
          if (!username) {
            throw new Error("No username provided");
          }
          const publicProfile = await api.users.get(username);
          setProfileData(publicProfile);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, [username, user, isCurrentUser]);

  // Clean up any temporary URLs when component unmounts
  useEffect(() => {
    return () => {
      if (tempIconUrl) {
        URL.revokeObjectURL(tempIconUrl);
      }
    };
  }, [tempIconUrl]);

  useEffect(() => {
    if (profileData) {
      displayName.setValue(profileData.display_name);
      if (profileData.username) {
        usernameField.setValue(profileData.username);
      }
    }
  }, [profileData]);

  // Handle window resize to toggle between mobile and desktop views
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSignOut = async (): Promise<void> => {
    try {
      await api.auth.logout();
      setIsLoggedIn(false);
      navigate('/login');
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const toggleMenu = (): void => {
    setMenuOpen(!menuOpen);
  };

  const handleBack = (): void => {
    fromChatId ? navigate(`/chats/${fromChatId}`) : navigate('/chats');
  };

  const componentProps: ProfileComponentProps = {
    profileData,
    isCurrentUser,
    isUpdating,
    displayName,
    usernameField,
    activeTab,
    characters,
    personas,
    scenarios,
    tabsLoading,
    menuOpen,
    handleIconChange,
    handleSignOut,
    toggleMenu,
    setActiveTab,
    refreshCharacters,
    refreshPersonas,
    refreshScenarios,
    fromChatId,
    handleBack,
    loading
  };

  return isMobile 
    ? <MobileUserProfile {...componentProps} /> 
    : <DesktopUserProfile {...componentProps} />;
}

export default UserProfile; 